import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { trackPurchase, trackTrialStart } from "../lib/tracking";

const PLAN_PRICE = { lite: 7.99, plus: 17.99, pro: 31.99 };

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const session_id = params.get("session_id");
    const [status, setStatus] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const polledRef = useRef(false);
    const convFired = useRef(false);
    const { checkAuth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!session_id || polledRef.current) return;
        polledRef.current = true;

        const fireConversion = (data) => {
            if (convFired.current) return;
            convFired.current = true;
            const value = (data.amount_total ? data.amount_total / 100 : 0) || PLAN_PRICE[data.plan_id] || 0;
            if (data.payment_status === "trialing") trackTrialStart(value, data.plan_id);
            else trackPurchase(value, data.plan_id);
        };

        const poll = async (att = 0) => {
            if (att > 10) {
                setStatus({ payment_status: "timeout" });
                return;
            }
            setAttempts(att + 1);
            try {
                const { data } = await api.get(`/checkout/status/${session_id}`);
                setStatus(data);
                if (data.payment_status === "paid" || data.payment_status === "trialing") {
                    fireConversion(data);
                    checkAuth();
                    return;
                }
                if (data.status === "expired" || data.payment_status === "unpaid") {
                    return;
                }
                setTimeout(() => poll(att + 1), 2000);
            } catch {
                setTimeout(() => poll(att + 1), 2000);
            }
        };
        poll();
    }, [session_id, checkAuth]);

    const trialing = status?.payment_status === "trialing";
    const paid = status?.payment_status === "paid" || trialing;
    const failed = status?.payment_status === "timeout" || status?.status === "expired" || status?.payment_status === "unpaid";
    const [needsNumber, setNeedsNumber] = useState(false);

    useEffect(() => {
        if (!paid) return;
        (async () => {
            try {
                const { data } = await api.get("/numbers/allowance");
                setNeedsNumber(data.numbers_owned === 0);
            } catch {}
        })();
    }, [paid]);

    return (
        <div className="max-w-xl mx-auto py-16" data-testid="billing-success-page">
            <div className="border border-zinc-200 rounded-sm bg-white p-10 text-center">
                {paid && (
                    <>
                        <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                        <h1 className="font-display text-3xl font-bold">
                            {trialing ? "Your free trial is active!" : "Payment confirmed"}
                        </h1>
                        <p className="text-zinc-600 mt-3">
                            {trialing
                                ? "Enjoy Flex Line free for 3 days. We'll only charge your card after the trial — cancel anytime before then."
                                : status.kind === "number"
                                ? "Your new phone number is ready in My Numbers."
                                : "Credits added to your account."}
                        </p>
                    </>
                )}
                {!status && (
                    <>
                        <div className="inline-block w-10 h-10 border-2 border-zinc-200 border-t-black rounded-full animate-spin mb-4" />
                        <h1 className="font-display text-3xl font-bold">Checking payment…</h1>
                    </>
                )}
                {status && !paid && !failed && (
                    <>
                        <Clock className="w-12 h-12 mx-auto text-amber-500 mb-4 animate-pulse" />
                        <h1 className="font-display text-3xl font-bold">Processing…</h1>
                        <p className="text-zinc-500 font-mono text-xs mt-3">Attempt {attempts}/10</p>
                    </>
                )}
                {failed && (
                    <>
                        <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                        <h1 className="font-display text-3xl font-bold">Payment not completed</h1>
                        <p className="text-zinc-600 mt-3">Try again from billing.</p>
                    </>
                )}

                {paid && needsNumber && (
                    <div className="mt-6 border border-[#D97706] bg-[#FEF3C7] rounded-sm p-4 text-left" data-testid="needs-number-banner">
                        <p className="font-medium text-[#D97706]">One more step — claim your included phone number</p>
                        <p className="text-sm text-zinc-600 mt-1">
                            Your subscription includes a number. Pick it now at no extra charge to start texting and calling.
                        </p>
                        <Button
                            onClick={() => navigate("/numbers")}
                            data-testid="pick-number-now-btn"
                            className="mt-3 w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-sm h-10"
                        >
                            Claim my number now
                        </Button>
                    </div>
                )}

                <div className="mt-8 flex gap-2 justify-center">
                    <Button
                        onClick={() => navigate("/my-numbers")}
                        data-testid="success-go-numbers"
                        className="bg-black text-white rounded-sm h-10"
                    >
                        Go to my numbers
                    </Button>
                    <Button
                        onClick={() => navigate("/billing")}
                        variant="outline"
                        className="rounded-sm border-zinc-300 h-10"
                    >
                        Billing
                    </Button>
                </div>
            </div>
        </div>
    );
}
